import { useState, useEffect } from 'react';

interface WaitlistModalProps {
  open: boolean;
  onClose: () => void;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function WaitlistModal({ open, onClose }: WaitlistModalProps) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setFirstName('');
      setEmail('');
      setStatus('idle');
      setErrorMsg('');
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) return;

    setStatus('submitting');
    setErrorMsg('');

    try {
      const body = new URLSearchParams({
        firstName: firstName.trim(),
        email: email.trim(),
        _subject: 'dial-back',
        _cc: 'pgits@gits.agency',
      });

      const res = await fetch('https://readdy.ai/api/form/d77bujornr7vi0ujqo60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ fontFamily: "'Instrument Sans', sans-serif" }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-pointer"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-[460px] rounded-[4px] overflow-hidden"
        style={{ background: '#141210', border: '1px solid rgba(245,242,236,0.08)' }}
      >
        {/* Top accent */}
        <div className="h-[3px] bg-[#c8472a] w-full" />

        <div className="p-10 max-md:p-7">
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-[#9a9488] hover:text-[#f5f2ec] transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>

          {status === 'success' ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5 rounded-full bg-[#c8472a]/10">
                <i className="ri-check-line text-2xl text-[#c8472a]" />
              </div>
              <h3
                className="text-[#f5f2ec] font-light mb-3"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '1.9rem' }}
              >
                You&apos;re on the list.
              </h3>
              <p className="text-[0.9rem] text-[rgba(245,242,236,0.55)] leading-[1.7]">
                We&apos;ll reach out as soon as early access opens. Keep an eye on your inbox.
              </p>
              <button
                onClick={onClose}
                className="mt-8 text-[12px] font-semibold tracking-[0.1em] uppercase text-[#9a9488] hover:text-[#f5f2ec] transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Eyebrow */}
              <div
                className="flex items-center gap-2 mb-5"
                style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#c8472a' }}
              >
                <span className="block w-5 h-px bg-[#c8472a]" />
                Dial-Back · Early Access
              </div>

              <h3
                className="font-light leading-[1.1] text-[#f5f2ec] mb-2"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 'clamp(1.8rem, 4vw, 2.4rem)' }}
              >
                Get early access.
              </h3>
              <p className="text-[0.88rem] text-[rgba(245,242,236,0.5)] leading-[1.65] mb-8">
                Beta pricing locked for life. We&apos;ll notify you the moment access opens — no spam, ever.
              </p>

              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="db-firstName"
                    className="text-[11px] tracking-[0.12em] uppercase text-[#9a9488]"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    First name
                  </label>
                  <input
                    id="db-firstName"
                    name="firstName"
                    type="text"
                    required
                    autoComplete="given-name"
                    placeholder="Jessica"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full bg-white/[0.04] border border-white/[0.1] rounded-[3px] px-4 py-3 text-[0.9rem] text-[#f5f2ec] placeholder-[#9a9488]/60 outline-none focus:border-[#c8472a]/60 transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="db-email"
                    className="text-[11px] tracking-[0.12em] uppercase text-[#9a9488]"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    Email address
                  </label>
                  <input
                    id="db-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white/[0.04] border border-white/[0.1] rounded-[3px] px-4 py-3 text-[0.9rem] text-[#f5f2ec] placeholder-[#9a9488]/60 outline-none focus:border-[#c8472a]/60 transition-colors"
                  />
                </div>

                {errorMsg && (
                  <p className="text-[0.82rem] text-[#c8472a]">{errorMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="mt-2 w-full text-[13px] font-semibold tracking-[0.1em] uppercase text-[#0e0d0b] bg-[#f5f2ec] py-4 rounded-sm transition-all duration-200 hover:bg-[#c8472a] hover:text-[#f5f2ec] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {status === 'submitting' ? 'Sending...' : 'Join the waitlist'}
                </button>

                <p
                  className="text-center text-[#9a9488] mt-1"
                  style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.06em' }}
                >
                  No credit card. No spam. Unsubscribe anytime.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
